<script>
  import { onMount } from 'svelte';
  import { fade } from 'svelte/transition';
  import Cookies from 'js-cookie';
  import Bowser from 'bowser';
  import { playerState, drmState, bannedState, streamState } from '../js/state.js';
  import {
    requestFullscreen,
    requestPlayVideo,
    requestPauseVideo,
    showVideoControls,
    hideVideoControls,
    postError,
    clearCommandRequest,
    setPlayerState,
    setSoundState,
    setPositionState,
  } from '../js/actions.js';
  import {
    ERROR_CRITICAL,
    PLAYER_STATE_PLAYING,
    PLAYER_STATE_PAUSED,
    PLAYER_CMD_PLAY,
    PLAYER_CMD_PAUSE,
    PLAYER_CMD_SOUND_MUTED,
    PLAYER_CMD_SOUND_ON,
    PLAYER_CMD_SOUND_SET_LEVEL,
    PLAYER_CMD_JUMP_TO_LIVE,
    PLAYER_CMD_JUMP_TO_POSITION,
    PLAYER_CMD_PLAY_IF_NOT_PAUSED,
    PLAYER_CMD_FULLSCREEN,
    PLAYER_STATUS_PLAY,
    PLAYER_STATUS_PAUSE,
    PLAYER_STATUS_ENDED,
    PLAYER_STATUS_ERROR,
    PLAYER_TYPE_UNKNOWN,
    PLAYER_TYPE_HLS_JS,
    PLAYER_TYPE_APPLE_MPEG,
    PLAYER_STATE_SOUND_MUTED,
    PLAYER_STATE_SOUND_ON,
    HOW_DELAYED_IS_TOO_DELAYED,
    DELAY_OK,
    DELAY_BEHIND,
    DELAY_AHEAD,
  } from '../js/constants.js';
  import {
    REACHED_ABUSE_LIMIT,
    USER_BANNED,
    UNSUPPORTED_BROWSER,
    PROBLEM_PLAYING_VIDEO_STABILITY,
    PROBLEM_PLAYING_VIDEO_SOURCE,
    PROBLEM_PLAYING_VIDEO_STABILITY_REFRESH_IF_PERSISTS,
  } from '../js/l10n/en_US.js';
  import videoDebugger from '../js/libs/debug/video.js';
  import StreamControls from './StreamControls.svelte';
  import Loading from './Loading.svelte';
  import LiveIndicator from './LiveIndicator.svelte';

  export let url = '',
    onResize = (w, h) => {},
    live = false;

  let video = null,
    hlsClient,
    Hls, // Dynamically imported hls.js package
    connectivityError = false,
    connectivityMessage = '',
    connectivityErrorTimeout = null,
    connectivityErrorRefreshTimeout = null,
    debounceTimer,
    videoMouseTimer = null,
    position = 0,
    duration = 0,
    previousValidityState = false,
    enableLogging = window.location.search && window.location.search.includes('enableLogging'),
    playerFormat = PLAYER_TYPE_UNKNOWN, // unknown, hlsjs, mpegurl
    debounceIntervalPeriod = 700,
    connectivityErrorTimeoutInterval = 3000,
    connectivityErrorRefreshTimeoutInterval = 30000,
    bowser = Bowser.parse(window.navigator.userAgent),
    vw = 0,
    vh = 0;

  const forceAppleMpeg = bowser.os.name == 'iOS' || bowser.browser.name === 'Safari',
    subtitleCookie = `${$streamState.source}_${$streamState.event_id}_subtitle`;

  function debouncedAttachedVideo(playImmediately) {
    try {
      debounceTimer = setTimeout(() => {
        attachVideo(playImmediately);
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }, debounceIntervalPeriod);
    } catch (e) {
      console.warn('[StreamPlayer] Unable to start video: ', e);
    }
  }

  function checkIfTooDelayed() {
    if (!(video && hlsClient && hlsClient.liveSyncPosition)) {
      return DELAY_OK;
    }
    let delay = hlsClient.liveSyncPosition - video.currentTime;
    if (delay < -1 * HOW_DELAYED_IS_TOO_DELAYED) {
      return DELAY_AHEAD;
    }
    if (delay > HOW_DELAYED_IS_TOO_DELAYED) {
      return DELAY_BEHIND;
    }
    return DELAY_OK;
  }

  function appendToken(url, token) {
    let parsed = new URL(url);
    if (parsed.searchParams.get('t')) {
      return url;
    }
    const glue = url.indexOf('?') === -1 ? '?' : '&';
    return url + glue + 't=' + token;
  }

  function attachVideo(playImmediately) {
    enableLogging && console.log('[StreamPlayer] Attaching video... ');

    if (!$drmState.token || ($bannedState && $bannedState.banned)) {
      enableLogging &&
        console.log(
          '[StreamPlayer] Refusing to attach video because token has not been set or user is banned.'
        );
      return;
    }

    if ($drmState && !$drmState.valid) {
      enableLogging &&
        console.log('[StreamPlayer] Refusing to attach video because DRM state is invalid.');
      return;
    }

    video = document.getElementById('video');
    if (!video) {
      enableLogging &&
        console.warn('[StreamPlayer] No video element present - DRM may have blocked the user');
      return;
    }

    enableLogging && videoDebugger(video, 1);
    playerState.setEnded(false);

    video.addEventListener('canplaythrough', (e) => {
      enableLogging &&
        console.log(
          '[StreamPlayer] Video Event: Fires when the browser can start playing the audio/video',
          e
        );

      $playerState.state !== PLAYER_STATE_PAUSED &&
        video &&
        playerFormat !== PLAYER_TYPE_APPLE_MPEG &&
        requestPlayVideo();
      playerState.setLoaded(true);
      playerState.setWaiting(false);
      clearConnectivityError();
    });

    // needed for firefox to play again after a disconnect because some times the events
    // get out of order
    video.addEventListener('progress', (e) => {
      clearConnectivityError();
    });

    video.addEventListener('ended', (e) => {
      playerState.setStatus(PLAYER_STATUS_ENDED);
      enableLogging && console.log('[StreamPlayer] Video has ended.');
      try {
        requestPauseVideo();
        playerState.setEnded(true);
      } catch (e) {}
    });

    video.addEventListener('waiting', (e) => {
      enableLogging &&
        console.log('[StreamPlayer] Video is waiting (needs to buffer the next frame)', e);
      playerState.setLoaded(false);
      playerState.setWaiting(true);
    });

    video.addEventListener('loadedmetadata', (e) => {
      // ios doesn't fire a canplay event on initial load
      if (!forceAppleMpeg) return;
      playerState.setLoaded(true);
      playerState.setWaiting(false);
    });

    video.addEventListener('abort', (e) => {
      playerState.setLoaded(false);
      playerState.setWaiting(true);
    });

    video.addEventListener('pause', (e) => {
      playerState.setStatus(PLAYER_STATUS_PAUSE);
    });

    video.addEventListener('play', (e) => {
      playerState.setStatus(PLAYER_STATUS_PLAY);
    });

    video.addEventListener('error', (e) => {
      playerState.setStatus(PLAYER_STATUS_ERROR);
    });

    url = appendToken(url, $drmState.token);

    if (!forceAppleMpeg && Hls.isSupported()) {
      playerFormat = PLAYER_TYPE_HLS_JS;

      try {
        hlsClient = new Hls();
      } catch (e) {
        console.log(e.toString());
        return;
      }

      hlsClient.loadSource(url);
      hlsClient.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, setHlsSubtitle);
      hlsClient.on(Hls.Events.MEDIA_ATTACHED, () => {
        enableLogging &&
          console.log('[StreamPlayer] Video Event: Video src attached', video && video.src);
        clearConnectivityError();
      });
      hlsClient.on(Hls.Events.ERROR, (evt, data) => {
        enableLogging && console.warn('hls error event', evt, 'data', data);
        markConnectivityError();
        if (data && data.details === 'bufferStalledError') {
          enableLogging &&
            console.log(
              'Safari bug with HLS.js. Attempt to reconnect. Patch is available in the next release from HLS.js.'
            );
        }

        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              // try to recover network error
              console.log(
                '[StreamPlayer] Video Event: Fatal network error encountered, try to recover'
              );
              hlsClient.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log(
                '[StreamPlayer] Video Event: Fatal media error encountered, try to recover'
              );
              hlsClient.recoverMediaError();
              break;
            default:
              console.log(
                '[StreamPlayer] Video Event: Fatal error encountered, enabled to recover'
              );
              postError(PROBLEM_PLAYING_VIDEO_SOURCE, ERROR_CRITICAL);
              destroyVideo();
              return;
          }
        }
      });

      try {
        enableLogging && console.log('[StreamPlayer] Video Event: Attaching video');
        hlsClient.attachMedia(video);
      } catch (e) {
        console.warn('[StreamPlayer] Video Event: Attaching error:', e);
        postError(PROBLEM_PLAYING_VIDEO_SOURCE, ERROR_CRITICAL);
      }
    } else if (video && video.canPlayType && video.canPlayType('application/vnd.apple.mpegurl')) {
      playerFormat = PLAYER_TYPE_APPLE_MPEG;
      video.setAttributeNode(document.createAttribute('controls'));
      video.src = url;
    } else {
      postError(UNSUPPORTED_BROWSER, ERROR_CRITICAL);
    }
  }

  function destroyVideo() {
    hlsClient && hlsClient.destroy();
    delete window.video;
    video = null;
  }

  // DOM Handlers
  function toggle() {
    if (!$drmState.valid || $playerState.waiting || playerFormat !== PLAYER_TYPE_HLS_JS) return;
    if ($playerState.state === PLAYER_STATE_PLAYING) {
      requestPauseVideo();
    } else {
      requestPlayVideo();
    }
  }

  function setHlsSubtitle() {
    let t = parseInt(Cookies.get(subtitleCookie) || -1);
    if (hlsClient && hlsClient.subtitleTracks.length >= t + 1) {
      setTimeout(function() {
        hlsClient.subtitleTrack = t;
      }, 100);
    }
  }

  function showControls() {
    showVideoControls();
    clearTimeout(videoMouseTimer);
    videoMouseTimer = setTimeout(function() {
      hideControls();
    }, 2000);
  }

  function hideControls() {
    hideVideoControls();
  }

  function markConnectivityError() {
    enableLogging && console.log('[StreamPlayer] Marking connectivity error state');
    connectivityError = true;
    if (!connectivityErrorTimeout) {
      // Don't alarm the user of a brief connectivity hiccup
      connectivityErrorTimeout = setTimeout(() => {
        connectivityMessage = PROBLEM_PLAYING_VIDEO_STABILITY;
      }, connectivityErrorTimeoutInterval);

      // If we've been stuck for a while, warn the user that they may want to refresh
      connectivityErrorRefreshTimeout = setTimeout(() => {
        connectivityMessage = PROBLEM_PLAYING_VIDEO_STABILITY_REFRESH_IF_PERSISTS;
      }, connectivityErrorRefreshTimeoutInterval);
    }
  }

  function clearConnectivityError() {
    enableLogging && console.log('[StreamPlayer] Clearing connectivity error state');
    connectivityError = false;
    clearTimeout(connectivityErrorTimeout);
    clearTimeout(connectivityErrorRefreshTimeout);
    connectivityErrorTimeout = null;
    connectivityMessage = '';
  }

  // Online / Offline Detection

  window.addEventListener('online', () => {
    clearConnectivityError();
  });

  window.addEventListener('offline', () => {
    markConnectivityError();
  });

  // Reactive Statements

  onMount(async () => {
    let mod = await import('hls.js');
    Hls = mod.default;
  });

  $: setPositionState(position); // map video.currentTime to $playerState

  $: onResize(vw, vh);

  $: if ($drmState.challenge) {
    clearConnectivityError();
  }

  $: if ($drmState.token && Hls && (!video || !video.src)) {
    // If we were previously blocked (or have yet to start playing) and now have a
    // valid DRM response, attach the video.
    if (!previousValidityState && ($drmState.valid || !$streamState.drm)) {
      enableLogging && console.log('[StreamPlayer] Attaching video');
      debouncedAttachedVideo(true);
    }

    if (previousValidityState && !$drmState.valid && $streamState.drm) {
      enableLogging && console.log('[StreamPlayer] Detaching video');
      destroyVideo();
    }
  }

  $: if ($bannedState && $bannedState.banned) {
    requestPauseVideo();
  }

  // Follow command pattern. This is the only way request state changes
  // should come into the player. Take in requests, update state. Keep
  // requests and state changes decoupled.
  $: if ($playerState.command && video) {
    enableLogging && console.log('[StreamPlayer] Player cmd issued:', $playerState.command);
    switch ($playerState.command) {
      case PLAYER_CMD_SOUND_MUTED:
        video.volume = 0;
        setSoundState(PLAYER_STATE_SOUND_MUTED);
        break;
      case PLAYER_CMD_SOUND_ON:
        video.volume = 1;
        setSoundState(PLAYER_STATE_SOUND_ON);
        break;
      case PLAYER_CMD_SOUND_SET_LEVEL:
        video.volume = $playerState.commandPayload;
        if (video.volume === 0) {
          setSoundState(PLAYER_STATE_SOUND_MUTED);
        } else {
          setSoundState(PLAYER_STATE_SOUND_ON);
        }
        break;
      case PLAYER_CMD_JUMP_TO_POSITION:
        if (video && video.duration && $playerState.commandPayload) {
          video.currentTime = $playerState.commandPayload;
          // Note: setPositionState is updated as the video fires
        }
        break;
      case PLAYER_CMD_JUMP_TO_LIVE:
        // TODO: does duration work instead?
        if (video && hlsClient && hlsClient.liveSyncPosition) {
          video.currentTime = hlsClient.liveSyncPosition;
          // Note: setPositionState is updated as the video fires
        }
        break;
      case PLAYER_CMD_PLAY:
        try {
          video.play();
          setPlayerState(PLAYER_STATE_PLAYING);
        } catch (e) {
          enableLogging && console.log('[StreamPlayer] Unable to play video: ', e);
        }
        break;
      case PLAYER_CMD_PAUSE:
        try {
          video.pause();
          setPlayerState(PLAYER_STATE_PAUSED);
        } catch (e) {
          enableLogging && console.log('[StreamPlayer] Unable to pause video: ', e);
        }
        break;
      case PLAYER_CMD_PLAY_IF_NOT_PAUSED:
        if ($playerState.state !== PLAYER_STATE_PAUSED) {
          requestPlayVideo();
        }
        break;
      case PLAYER_CMD_FULLSCREEN:
        try {
          if (document.fullscreenElement) {
            document
              .exitFullscreen()
              .then(
                () => enableLogging && console.log('[StreamPlayer] Exited from Full screen mode')
              )
              .catch((err) => enableLogging && console.error(err));
            break;
          }

          const elem = document.getElementById('video-container');
          if (elem.requestFullscreen) {
            elem.requestFullscreen();
          } else if (elem.mozRequestFullScreen) {
            elem.mozRequestFullScreen();
          } else if (elem.webkitRequestFullscreen) {
            elem.webkitRequestFullscreen();
          } else if (elem.msRequestFullscreen) {
            elem.msRequestFullscreen();
          }
          setFullscreenStateFullscreen();
        } catch (e) {
          enableLogging && console.log('[StreamPlayer] Failed to go full screen due to ', e);
        }
        break;
    }
    clearCommandRequest();
  }

  setInterval(() => {
    playerState.setDelayStatus(checkIfTooDelayed());
  }, 1000);

  if (!forceAppleMpeg) {
    if ('orientation' in screen) {
      screen.orientation.addEventListener('change', function() {
        if (screen.orientation.type.startsWith('landscape')) {
          requestFullscreen();
        } else if (document.fullscreenElement) {
          document.exitFullscreen();
        }
      });
    }
  }
</script>

<div>
  {#if !$drmState || $drmState.valid}
    <div
      style={'display:' + (($drmState && !$drmState.valid) || ($bannedState && $bannedState.banned) ? 'none' : 'block')}
      id="video-container"
      on:mouseleave={hideControls}
      on:mousemove={showControls}
      bind:clientWidth={vw}
      bind:clientHeight={vh}>

      <div class="video-container-div">
        {#if live && bowser.platform.type === 'desktop'}
          <LiveIndicator floating={true} />
        {/if}
        <!-- svelte-ignore a11y-media-has-caption -->
        <video
          id="video"
          preload="auto"
          webkit-playsinline="true"
          playsinline="true"
          on:click|preventDefault={toggle}
          bind:currentTime={position}
          bind:duration />

        {#if $playerState.waiting || connectivityError}
          <div class="video-overlay">
            <div class="video-overlay-center">
              <Loading
                message={connectivityMessage}
                darkMode={!!connectivityMessage}
                hideIcon={!!connectivityMessage} />
            </div>
          </div>
        {/if}

        {#if video && video.duration && $playerState && !$playerState.waiting && $playerState.controls && playerFormat !== PLAYER_TYPE_APPLE_MPEG}
          <div class="video-overlay" transition:fade>
            <StreamControls
              {hlsClient}
              duration={video.duration}
              {position}
              volume={video.volume} />
          </div>
        {/if}
      </div>
    </div>
  {/if}

  {#if $drmState && !$drmState.valid}
    <div class="player-banner player-banner-dark player-abuse">{REACHED_ABUSE_LIMIT}</div>
  {:else if $bannedState && $bannedState.banned}
    <div class="player-banner player-banner-dark player-banned">{USER_BANNED}</div>
  {/if}
</div>
